import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { User } from './core/user/user.types';
import { UserService } from './core/user/user.service';
import {
	AngularPlugin,
	IErrorService,
} from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import * as Sentry from '@sentry/angular';

@Component({
	selector: 'cm-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [RouterOutlet],
})
export class AppComponent {
	private userService = inject(UserService);
	private router = inject(Router);

	/**
	 * Constructor
	 */
	constructor() {
		effect(() => {
			const user = this.userService.userSig$();
			if (user) {
				this.addAppInsights(user);
				Sentry.setUser({
					id: user.id.toString(),
					email: user.email,
					username: user.name,
				});
				Sentry.setTags({
					'claimmosaic.role': user.role,
					'claimmosaic.account_tier': user.accountTier?.toString(),
				});
				Sentry.setContext('claimmosaic.user', {
					customerId: user.customerId,
					contactId: user.contactId,
					currentTenantId: user.tenantId,
					primaryTenantId: user.primaryTenant,
					role: user.role,
					accountTier: user.accountTier,
					substatus: user.substatus,
					isLocked: user.isLocked,
				});
				Sentry.setContext('claimmosaic.company', {
					currentTenantCompanyName: user.currentTenantCompanyName,
					primaryTenantCompanyName: user.primaryTenantCompanyName,
				});
			} else {
				Sentry.setUser(null);
				Sentry.setTags({
					'claimmosaic.role': undefined,
					'claimmosaic.account_tier': undefined,
				});
				Sentry.setContext('claimmosaic.user', null);
				Sentry.setContext('claimmosaic.company', null);
			}
		});
	}

	addAppInsights(user: User) {
		var angularPlugin = new AngularPlugin();

		const appInsights = new ApplicationInsights({
			config: {
				connectionString: environment.appInsightsConnectionString,
				extensions: [<any>angularPlugin],
				extensionConfig: {
					[angularPlugin.identifier]: {
						router: this.router,
						errorServices: [new CustomErrorHandler()],
					},
				},
			},
		});

		appInsights.loadAppInsights();

		if (user?.email) {
			appInsights.setAuthenticatedUserContext(
				user.email,
				user.id.toString(),
				false,
			);
		}
	}
}

export class CustomErrorHandler implements IErrorService {
	private readonly _userService = inject(UserService);

	isAdmin$ = this._userService.isAdmin;

	handleError(error: any) {
		if (error && (!environment.production || this.isAdmin$())) {
			console.error(error);
		}
	}
}
